import dayjs from 'dayjs';

// Pipeline stages that group the 26 individual lead statuses into 6 visual columns.
// Keep in sync with server/controllers/leadPipeline.js PIPELINE_STAGES.
export const PIPELINE_STAGES = [
  {
    id: 'new',
    label: 'New',
    color: '#2065D1',
    statuses: ['New'],
  },
  {
    id: 'contacted',
    label: 'Contacted',
    color: '#1890FF',
    statuses: [
      'Contacted',
      'Call 1 - No Answer',
      'Call 1 - Left Message',
      'Call 2 - No Answer',
      'Call 2 - Left Message',
      'Follow-up Required',
    ],
  },
  {
    id: 'qualified',
    label: 'Qualified',
    color: '#54D62C',
    statuses: [
      'Qualified',
      'Pre-Approved - Active Shop...',
      'Pre-Approved - Under Contr...',
      'Application Pending',
      'Application Received',
      'Credit Repair',
    ],
  },
  {
    id: 'proposal',
    label: 'Proposal / Active',
    color: '#FFC107',
    statuses: [
      'On Hold',
      'Campaign - FHA Streamline ...',
      'Campaign - VA IRRRL Alert',
      'Campaign - Monthly Touchbase',
      'Campaign - Quarterly Check...',
    ],
  },
  {
    id: 'converted',
    label: 'Converted / Won',
    color: '#229A16',
    statuses: ['Converted', 'Closed/Won'],
  },
  {
    id: 'lost',
    label: 'Lost / Dead',
    color: '#FF4842',
    statuses: [
      'Closed/Lost',
      'Not Qualified',
      'Not Interested',
      'Call 1 - Not Interested',
      'Call 2 - Not Interested',
      'Dead Lead',
    ],
  },
];

// Flat list of all lead statuses across every stage
export const ALL_LEAD_STATUSES = PIPELINE_STAGES.flatMap((s) => s.statuses);

/**
 * Map a leadStatus string to its parent pipeline stage object.
 * Returns the 'new' stage as fallback for unknown statuses.
 */
export function getStageForStatus(status) {
  const match = PIPELINE_STAGES.find((stage) => stage.statuses.includes(status));
  return match || PIPELINE_STAGES[0];
}

/** Get the hex color for a pipeline stage ID. */
export function getStageColor(stageId) {
  const stage = PIPELINE_STAGES.find((s) => s.id === stageId);
  return stage ? stage.color : '#919EAB';
}

/**
 * Determine urgency level based on days since last activity.
 * green  = 0-3 days  (active)
 * yellow = 4-7 days  (aging)
 * red    = 8+ days or null  (stale)
 */
export function getIdleUrgency(lastActionDate) {
  if (!lastActionDate) return 'red';
  const days = dayjs().diff(dayjs(lastActionDate), 'day');
  if (days <= 3) return 'green';
  if (days <= 7) return 'yellow';
  return 'red';
}

/** Map urgency string to a hex color. */
export function getUrgencyColor(urgency) {
  switch (urgency) {
    case 'green':
      return '#54D62C';
    case 'yellow':
      return '#FFC107';
    case 'red':
      return '#FF4842';
    default:
      return '#919EAB';
  }
}

/** Get days since last activity as a human-readable string. */
export function getDaysSinceActivity(lastActionDate) {
  if (!lastActionDate) return 'No activity';
  const days = dayjs().diff(dayjs(lastActionDate), 'day');
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/** Pre-defined quick filter configurations. */
export const QUICK_FILTERS = [
  {
    id: 'my-leads',
    label: 'My Leads',
    icon: 'mdi:account',
    filterFn: (lead, userId) => lead.assigned_agent === userId || lead.assigned_agent?._id === userId,
  },
  {
    id: 'new-today',
    label: 'New Today',
    icon: 'mdi:new-box',
    filterFn: (lead) =>
      lead.leadStatus === 'New' && dayjs(lead.createdOn).isSame(dayjs(), 'day'),
  },
  {
    id: 'needs-followup',
    label: 'Needs Follow-Up',
    icon: 'mdi:bell-alert',
    filterFn: (lead) => {
      if (lead.leadStatus === 'Follow-up Required') return true;
      if (!lead.lastActionDate) return true;
      return dayjs().diff(dayjs(lead.lastActionDate), 'day') >= 7;
    },
  },
  {
    id: 'hot-leads',
    label: 'Hot Leads',
    icon: 'mdi:fire',
    filterFn: (lead) => (lead.leadScore || 0) >= 4,
  },
];
